import { useEffect, useRef, useState } from 'react'
import { Check, Pencil } from 'lucide-react'
import Layers from '../../../assets/icons/layers.svg'
import ZoomOutDisabled from '../../../assets/icons/zoom-out-disabled.svg'
import ZoomOutEnabled from '../../../assets/icons/zoom-out-enabled.svg'
import FullScreenDisabled from '../../../assets/icons/full-screen-disabled.svg'
import FullScreenEnabled from '../../../assets/icons/full-screen-enabled.svg'
import ZoomInEnabled from '../../../assets/icons/zoom-in-enabled.svg'
import ZoomInDisabled from '../../../assets/icons/zoom-in-disabled.svg'
import { useDispatch, useSelector } from 'react-redux'
import { modifyLeftEyeCurrentImage, modifyRightEyeCurrentImage, patchImageInEye } from '../../../redux/images'
import { useRestoreImage } from '../../../hooks/useRestoreImage'
import { useModal } from '../../../hooks/useModal'
import {
  Container,
  PrincipalImageInfo,
  PrincipalImageText,
  ImagesContainer,
  ThumbnailsContainer,
  ThumbnailCounterWrapper,
  ThumbnailWrapper,
  ThumbnailImage,
  PrincipalImageContainer,
  PrincipalImage,
  ImageContainer,
  FloatingMenu,
  IAToggler,
  TogglerText,
  TogglerIcon,
  StatisticsWrapper,
  StatisticsElement,
  Title,
  StatisticsText,
  StatisticsPercentage,
  PercentageStatisticsContainer,
  PrincipalImageContainerPercentage,
  PredictionCard,
  CardHeader,
  CardTitle,
  CardBadge,
  SecondaryMetricsGrid,
  SecondaryMetric,
  SecondaryMetricLabel,
  SecondaryMetricBar,
  QualityActionButton,
  QualityActionText,
  QualityMenu,
  QualityMenuTitle,
  QualityMenuOption
} from './styles'
import { PercentageValues } from '../ImagePercentage/PercentageValues'
import ImageFull from '../ImagesUpload/ImageFullScreen/ImageFullScreenModal'
import QualityIndicatorBadge from '../ImagesUpload/QualityIndicatorBadge'
import { Loading } from '../../Loading'

const QUALITY_OPTIONS = [
  { value: 'buena', label: 'Buena calidad' },
  { value: 'aceptable', label: 'Calidad aceptable' },
  { value: 'mala', label: 'Mala calidad' }
]

const QUALITY_STATUS_BY_VALUE = {
  mala: 0,
  buena: 1,
  aceptable: 2
}

const QUALITY_LABEL_BY_VALUE = {
  buena: 'Buena',
  aceptable: 'Aceptable',
  mala: 'Mala'
}

const QUALITY_RIBBON_TEXT_BY_VALUE = {
  buena: 'Buena calidad',
  aceptable: 'Calidad aceptable',
  mala: 'Mala calidad'
}

export function ImagesReceived ({
  isVisible,
  whichEye,
  onlyIAImages,
  dr,
  glaucoma,
  manualImageQuality,
  onManualImageQualityChange,
  allowManualQualitySelection = false,
  showIAQualityIndicators = false
}) {
  const { leftEye, rightEye } = useSelector(state => state.images)
  const [imagesToShow, setImagesToShow] = useState([])
  const [fullScreenIcon, setFullScreenIcon] = useState(FullScreenDisabled)
  const [zoomInIcon, setZoomInIcon] = useState(ZoomInDisabled)
  const [zoomOutIcon, setZoomOutIcon] = useState(ZoomOutDisabled)
  const [IAFullScreen, setIAFullScreen] = useState(null)
  const [currentManualQualityValue, setCurrentManualQualityValue] = useState('')
  const [zoom, setZoom] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [dragging, setDragging] = useState(false)
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 })
  const [showQualityMenu, setShowQualityMenu] = useState(false)
  const [modalRef, openModal, closeModal] = useModal()
  const dispatch = useDispatch()
  const iaRequestIdRef = useRef(0)
  const { getHeatMapImagesWithState } = useRestoreImage()
  const EYE_REFERENCE = {
    left: {
      currentImage: leftEye.currentImage,
      setCurrentImage: modifyLeftEyeCurrentImage,
      images: leftEye.images
    },
    right: {
      currentImage: rightEye.currentImage,
      setCurrentImage: modifyRightEyeCurrentImage,
      images: rightEye.images
    }
  }
  const { currentImage: currentSelectedImage, images } = EYE_REFERENCE[whichEye]
  const currentQualityLabel = QUALITY_LABEL_BY_VALUE[currentManualQualityValue] || null
  const hasManualQualityDecision = Boolean(currentManualQualityValue && QUALITY_STATUS_BY_VALUE[currentManualQualityValue] !== undefined)
  const currentRibbonStatus = hasManualQualityDecision
    ? QUALITY_STATUS_BY_VALUE[currentManualQualityValue]
    : currentSelectedImage && currentSelectedImage.status
  const currentRibbonSourceLabel = hasManualQualityDecision ? 'Manual' : 'IA'
  const currentRibbonText = hasManualQualityDecision
    ? QUALITY_RIBBON_TEXT_BY_VALUE[currentManualQualityValue]
    : ''
  const currentImageSlot = images.find(img => img.id === currentSelectedImage?.id) || null
  const currentAiAssetState = currentImageSlot?.aiAssetState || 'idle'

  const handleSelectPrincipalImage = (thumbnail) => {
    setZoom(1)
    const { setCurrentImage } = EYE_REFERENCE[whichEye]
    dispatch(setCurrentImage(thumbnail))
  }

  const persistManualQuality = (qualityValue) => {
    if (!onManualImageQualityChange || !currentSelectedImage || !currentSelectedImage.image) return

    onManualImageQualityChange(currentSelectedImage.id, qualityValue, {
      eye: whichEye,
      position: currentSelectedImage.position,
      name: currentSelectedImage.name
    })
  }

  const handleManualQualitySelect = (qualityValue) => {
    persistManualQuality(qualityValue)
    setShowQualityMenu(false)
  }

  const toggleQualityMenu = () => {
    setShowQualityMenu((previousValue) => !previousValue)
  }
  const handleMouseDown = (e) => {
    e.preventDefault()
    if (zoom > 1) {
      setDragging(true)
      setStartPosition({ x: e.clientX, y: e.clientY })
    }
  }

  useEffect(() => {
    if (currentSelectedImage !== null && manualImageQuality[currentSelectedImage.id]) {
      setCurrentManualQualityValue(
        manualImageQuality[currentSelectedImage.id].value
      )
    }
  }, [manualImageQuality, currentSelectedImage])

  const handleMouseMove = (e) => {
    if (dragging) {
      const deltaX = e.clientX - startPosition.x
      const deltaY = e.clientY - startPosition.y
      setPosition(prevPosition => ({
        x: prevPosition.x + deltaX,
        y: prevPosition.y + deltaY
      }))
      setStartPosition({ x: e.clientX, y: e.clientY })
    }
  }

  const handleMouseUp = () => {
    setDragging(false)
  }

  // useEffect(() => {
  //   const currentTypeId = currentSelectedImage.id
  //   const nextCurrentImage = IMAGES_SELECT[imagesType].find(image => image.id === currentTypeId)

  //   setCurrentSelectImage(nextCurrentImage)
  // }, [imagesType])

  const handleChangeZoom = (amount) => {
    setZoom((prev) => Math.max(1, prev + amount))
  }

  const handleCloseFullScreen = () => {
    setIAFullScreen(null)
    closeModal()
  }

  const handleOpenFullScreen = async (IA) => {
    setIAFullScreen(IA)
    if (!IA || !currentSelectedImage?.id) return

    const eyeKey = whichEye === 'left' ? 'leftEye' : 'rightEye'
    const slot = images.find(img => img.id === currentSelectedImage.id)
    const assetState = slot?.aiAssetState || 'idle'

    if (assetState !== 'idle') return

    const requestId = ++iaRequestIdRef.current
    const imageId = currentSelectedImage.id

    dispatch(patchImageInEye(eyeKey, imageId, { aiAssetState: 'loading' }))
    const result = await getHeatMapImagesWithState(imageId)

    if (requestId !== iaRequestIdRef.current) {
      if (result.state === 'ready') {
        URL.revokeObjectURL(result.NHM)
        URL.revokeObjectURL(result.HM)
      }
      return
    }

    if (result.state === 'ready') {
      dispatch(patchImageInEye(eyeKey, imageId, {
        heatMapImage: { NHM: result.NHM, HM: result.HM },
        aiAssetState: 'ready'
      }))
    } else {
      dispatch(patchImageInEye(eyeKey, imageId, { aiAssetState: result.state }))
    }
  }

  useEffect(() => {
    if (zoom === 1) {
      setPosition({ x: 0, y: 0 })
    }
  }, [zoom])

  useEffect(() => {
    if (IAFullScreen !== null) {
      openModal()
    }
  }, [IAFullScreen])

  useEffect(() => {
    setShowQualityMenu(false)
  }, [currentSelectedImage && currentSelectedImage.id, isVisible])

  useEffect(() => {
    const withContent = images.filter(thumbnail => thumbnail.image || thumbnail.restoreState === 'pending' || thumbnail.restoreState === 'loading')
    setImagesToShow(withContent)
    const hasValidSelection = currentSelectedImage && withContent.some(img => img.id === currentSelectedImage.id)
    if (!hasValidSelection) {
      const firstReady = withContent.find(img => img.image)
      handleSelectPrincipalImage(firstReady || withContent[0] || images[0])
    }
  }, [images])

  return (
    <Container isVisible={isVisible}>
      <ImageFull
        control
        IA={IAFullScreen}
        image={currentSelectedImage?.image}
        heatmapImage={currentImageSlot?.heatMapImage || null}
        aiAssetState={currentAiAssetState}
        close={handleCloseFullScreen}
        ref={modalRef}
      />

      <PrincipalImageInfo>
        {currentSelectedImage
          ? <PrincipalImageText> {currentSelectedImage.name} </PrincipalImageText>
          : null}
      </PrincipalImageInfo>

      <ImagesContainer>
        <ThumbnailsContainer>
          {imagesToShow.map((thumbnail) => (
            <ThumbnailCounterWrapper key={thumbnail.id}>
              <ThumbnailWrapper
                image={thumbnail.image}
                selected={currentSelectedImage?.id === thumbnail.id}
                $showQualityStatus={showIAQualityIndicators}
                $status={thumbnail.status}
                onClick={() => handleSelectPrincipalImage(thumbnail)}
              >
                {thumbnail.image
                  ? <ThumbnailImage src={thumbnail.image} alt='add-image' />
                  : <Loading size={28} />}
              </ThumbnailWrapper>
            </ThumbnailCounterWrapper>
          ))}
        </ThumbnailsContainer>
        <PrincipalImageContainerPercentage>

          {imagesToShow.length > 0
            ? (
              <PrincipalImageContainer inTheCenter={currentSelectedImage}>
                {currentSelectedImage?.image && (
                  <FloatingMenu>
                    {!onlyIAImages && (
                      <IAToggler onClick={() => handleOpenFullScreen(true)}>
                        <TogglerText> IA </TogglerText>
                        <TogglerIcon src={Layers} alt='toggle ia' />
                      </IAToggler>
                    )}

                    <IAToggler active withGap>
                      <TogglerIcon
                        onMouseEnter={() => setZoomOutIcon(ZoomOutEnabled)}
                        onMouseLeave={() => setZoomOutIcon(ZoomOutDisabled)}
                        onClick={() => handleChangeZoom(-0.1)}
                        src={zoomOutIcon} alt='zoom out'
                      />
                      <TogglerIcon
                        onMouseEnter={() => setZoomInIcon(ZoomInEnabled)}
                        onMouseLeave={() => setZoomInIcon(ZoomInDisabled)}
                        onClick={() => handleChangeZoom(0.1)}
                        src={zoomInIcon} alt='zoom in'
                      />
                      <TogglerIcon
                        onMouseEnter={() => setFullScreenIcon(FullScreenEnabled)}
                        onMouseLeave={() => setFullScreenIcon(FullScreenDisabled)}
                        onClick={() => handleOpenFullScreen(false)}
                        src={fullScreenIcon}
                        alt='full screen'
                      />
                    </IAToggler>
                  </FloatingMenu>
                )}
                {showIAQualityIndicators && currentSelectedImage && currentSelectedImage.image && (
                  <QualityIndicatorBadge
                    status={currentRibbonStatus}
                    sourceLabel={currentRibbonSourceLabel}
                    textOverride={currentRibbonText}
                    align='right'
                    top={16}
                    offset={16}
                  />
                )}
                <ImageContainer
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                >
                  {currentSelectedImage?.image
                    ? <PrincipalImage zoom={zoom} x={position.x} y={position.y} src={currentSelectedImage.image} alt='principal-image' />
                    : (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                        <Loading size={48} />
                        <p style={{ color: 'white', fontSize: '0.875rem', fontWeight: 500, margin: 0, textAlign: 'center' }}>Cargando imágenes del estudio. Por favor espere.</p>
                      </div>
                      )}
                </ImageContainer>
                {allowManualQualitySelection && currentSelectedImage && currentSelectedImage.image && (
                  <>
                    <QualityActionButton
                      type='button'
                      onClick={toggleQualityMenu}
                      $quality={currentManualQualityValue}
                      title='Reportar calidad de imagen'
                    >
                      <Pencil size={14} />
                      <QualityActionText>
                        {currentQualityLabel ? `Calidad: ${currentQualityLabel}` : 'Reportar calidad'}
                      </QualityActionText>
                    </QualityActionButton>

                    {showQualityMenu && (
                      <QualityMenu>
                        <QualityMenuTitle>Calidad de la imagen seleccionada</QualityMenuTitle>

                        <QualityMenuOption
                          type='button'
                          onClick={() => handleManualQualitySelect('')}
                          $active={!currentManualQualityValue}
                        >
                          <span>Sin definir</span>
                          {!currentManualQualityValue && <Check size={14} />}
                        </QualityMenuOption>

                        {QUALITY_OPTIONS.map((option) => (
                          <QualityMenuOption
                            key={option.value}
                            type='button'
                            onClick={() => handleManualQualitySelect(option.value)}
                            $active={currentManualQualityValue === option.value}
                            $quality={option.value}
                          >
                            <span>{option.label}</span>
                            {currentManualQualityValue === option.value && <Check size={14} />}
                          </QualityMenuOption>
                        ))}
                      </QualityMenu>
                    )}
                  </>
                )}
              </PrincipalImageContainer>
              )
            : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                <Loading size={100} />
                <p style={{ color: '#333', fontSize: '0.875rem', fontWeight: 500, margin: 0 }}>Cargando imágenes del estudio. Por favor espere.</p>
              </div>
              )}
          <PercentageStatisticsContainer>
            {imagesToShow.length > 0
              ? (
                <>
                  <PredictionCard>
                    <CardHeader>
                      <CardTitle $primary>Predicciones por IA</CardTitle>
                      <CardBadge>Automático</CardBadge>
                    </CardHeader>

                    <StatisticsWrapper>
                      {imagesToShow.length > 0 && (
                        <StatisticsElement display={dr ? 1 : 0}>
                          <StatisticsText>
                            Riesgo de retinopatía diabética
                          </StatisticsText>
                          <StatisticsPercentage>
                            <PercentageValues values={currentSelectedImage && currentSelectedImage.probabilidad} dxIA={currentSelectedImage && currentSelectedImage.riesgoIA} />
                          </StatisticsPercentage>
                        </StatisticsElement>
                      )}

                      <StatisticsElement display={glaucoma ? 1 : 0}>
                        <StatisticsText>
                          Riesgo de glaucoma
                        </StatisticsText>
                        <StatisticsPercentage>
                          <PercentageValues values='0.2' />
                        </StatisticsPercentage>
                      </StatisticsElement>
                    </StatisticsWrapper>

                    {currentSelectedImage && (
                      <SecondaryMetricsGrid>
                        <SecondaryMetric>
                          <SecondaryMetricLabel>Contraste</SecondaryMetricLabel>
                          <SecondaryMetricBar>
                            <PercentageValues values={currentSelectedImage.contraste} />
                          </SecondaryMetricBar>
                        </SecondaryMetric>

                        <SecondaryMetric>
                          <SecondaryMetricLabel>Iluminación</SecondaryMetricLabel>
                          <SecondaryMetricBar>
                            <PercentageValues values={currentSelectedImage.iluminacion} />
                          </SecondaryMetricBar>
                        </SecondaryMetric>

                        <SecondaryMetric>
                          <SecondaryMetricLabel>Enfoque</SecondaryMetricLabel>
                          <SecondaryMetricBar>
                            <PercentageValues values={currentSelectedImage.enfoque} />
                          </SecondaryMetricBar>
                        </SecondaryMetric>
                      </SecondaryMetricsGrid>
                    )}
                  </PredictionCard>
                </>
                )
              : (
                <Title>Cargando estudios. Espere por favor...</Title>
                )}
          </PercentageStatisticsContainer>
        </PrincipalImageContainerPercentage>
      </ImagesContainer>
    </Container>
  )
}

export default ImagesReceived
